import React, { memo, useCallback, useState } from "react";
import Spinner from "../../Spinner";
import MetricTrendChart from "../../charts/MetricTrendChart";
import SpirometryResultRow from "../rows/SpirometryResultRow";
import { useSpirometryResults } from "../../../hooks/results/useSpirometryResults";
import { useTranslation } from "react-i18next";
import { getValidatedMeasurementValue } from "../../../utils/results/validatedRanges";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import { SPIROMETRY_TREND_METRICS } from "../../../utils/constants";
import { useResultRowNavigation } from "../../../hooks/results/useResultRowNavigation";
import { RESULT_MODE_CONFIG } from "../../../utils/results/resultModeConfig";
import ResultsTabState from "../ResultsTabState";
import { useDailyMetricTrend } from "../../../hooks/results/useDailyMetricTrend";

const CONFIG = RESULT_MODE_CONFIG.spirometry;

export default memo(function SpirometryResults({ db, uid, firstRunByVariant }) {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState("FEV1");
  const metricConfig =
    SPIROMETRY_TREND_METRICS[metric] || SPIROMETRY_TREND_METRICS.FEV1;
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);

  const resetKey = `${uid || ""}:${CONFIG.resetKey}`;

  const { rows, loading, loadingMore, hasMore, error } = useSpirometryResults(
    db,
    uid,
    {
      pageSize,
    },
  );

  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useResultRowNavigation({
    variant: CONFIG.variant,
  });

  // Daily averages + min/max per selected metric (same logic as your pulse daily aggregation)
  const getSpirometryTrendValue = useCallback(
    (row) =>
      getValidatedMeasurementValue(
        metricConfig.rangeKey,
        row?.[metricConfig.valueKey],
      ),
    [metricConfig],
  );

  const trend = useDailyMetricTrend({
    rows,
    language: i18n.language,
    getValue: getSpirometryTrendValue,
  });

  const yUnit = metricConfig.unit;
  const yDecimals = metricConfig.decimals;
  const lineLabel = t(metricConfig.averageKey, metricConfig.averageFallback);

  return (
    <ResultsTabState
      uid={uid}
      loading={loading}
      error={error}
      rows={rows}
      emptyMode={CONFIG.mode}
      firstRunByVariant={firstRunByVariant}
    >
      <div className="w-full h-full min-h-0 flex flex-col gap-2">
        {SHOW_FULL_RESULTS && (
          <>
            {/* Metric toggle (only affects chart) */}
            <div className="w-full flex flex-wrap gap-2 pt-2">
              {Object.entries(SPIROMETRY_TREND_METRICS).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  className={[
                    "px-2 py-1 rounded-xl text-sm",
                    metric === key
                      ? "bg-primary text-greyscale-1"
                      : "bg-greyscale-1 text-primary",
                  ].join(" ")}
                  onClick={() => setMetric(key)}
                >
                  {t(config.labelKey, config.labelFallback)}
                </button>
              ))}
            </div>

            {/* Trend chart */}
            <MetricTrendChart
              height={190}
              labels={trend?.labels}
              values={trend?.values}
              xValues={trend?.xValues}
              xTickFormat="dd/MM"
              lineLabel={lineLabel}
              yUnit={yUnit}
              yDecimals={yDecimals}
              showMinMaxBars={true}
              minMax={trend?.minMax}
              counts={trend?.counts}
              showTrendLine={true}
            />
          </>
        )}

        {/* scrolling list */}
        <div
          aria-label="spirometry results list"
          className="w-full flex-1 min-h-0 overflow-y-auto"
          onScroll={handleScroll}
        >
          {rows.map((r, idx) => (
            <SpirometryResultRow
              key={r.id ?? idx}
              time={r.time}
              date={r.date}
              fev1={r.fev1}
              fvc={r.fvc}
              fev1FvcRatio={r.fev1FvcRatio}
              pef={r.pef}
              rr={r.rr}
              signalQuality={r.signalQuality}
              spirometryQuality={r.spirometryQuality}
              onClick={() => onRowClick(r)}
            />
          ))}
          {loadingMore ? <Spinner size={24} className="py-4" /> : null}
        </div>
      </div>
    </ResultsTabState>
  );
});
